import { useCallback } from 'react'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks'
import { AddNoteFormModel } from '@user/components/Notes/AddNoteDrawer/typings'
import { validateNote } from '@user/components/Notes/EditNoteDrawer/utils/validateNote'
import { createLeadNote } from '@user/store/leadNotes/api'
import { CreateLeadNoteInput } from '@user/store/leadNotes/typings'
import { CreateOpportunityNoteInput } from '@user/store/opportunityNotes/typings'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { createOpportunityNote } from '@user/store/opportunityNotes/api'

type Props = {
  entityId: number
  entityType: ENTITY_TYPES
  onUpdateSuccess?: () => void
  onGoBack?: () => void
  onClose: () => void
  serviceOptions: LocationDTO[]
}

export function useHandlers(props: Props) {
  const {
    entityId,
    entityType,
    onUpdateSuccess,
    onClose,
    serviceOptions,
    onGoBack,
  } = props

  const dispatch = useAppDispatch()

  const onSubmitLeadNote = useCallback(
    async (model: AddNoteFormModel) => {
      const { detail, title, addNotesToPestPac } = model
      const requestBody = {
        detail,
        id: 0,
        title: title || '',
      }
      if (entityType == ENTITY_TYPES.Lead) {
        let data: CreateLeadNoteInput = {
          ...requestBody,
          leadId: entityId,
        }
        if (addNotesToPestPac) {
          data = {
            ...data,
            ...getNoteUpdateObject({ data, model, serviceOptions }),
          }
        }
        const response = await dispatch(createLeadNote(data))

        if (createLeadNote.fulfilled.match(response)) {
          toast.success(`Note created.`)
          onUpdateSuccess && onUpdateSuccess()
          onGoBack ? onGoBack() : onClose()
        } else {
          toast.error(`Failed to create note.`)
        }
      }
      if (entityType == ENTITY_TYPES.Opportunity) {
        let data: CreateOpportunityNoteInput = {
          ...requestBody,
          opportunityId: entityId,
        }
        if (addNotesToPestPac) {
          data = {
            ...data,
            ...getNoteUpdateObject({ data, model, serviceOptions }),
          }
        }

        const response = await dispatch(createOpportunityNote(data))

        if (createOpportunityNote.fulfilled.match(response)) {
          toast.success(`Note created.`)
          onUpdateSuccess && onUpdateSuccess()
          onGoBack ? onGoBack() : onClose()
        } else {
          toast.error(`Failed to create note.`)
        }
      }
    },
    [
      onGoBack,
      entityType,
      dispatch,
      entityId,
      onUpdateSuccess,
      serviceOptions,
      onClose,
    ],
  )

  const validate = useCallback((model: AddNoteFormModel) => {
    return { ...validateNote(model) }
  }, [])

  return {
    onSubmitLeadNote,
    validate,
  }
}

interface getNoteUpdateObjectProps {
  data: CreateOpportunityNoteInput | CreateLeadNoteInput
  model: AddNoteFormModel
  serviceOptions: LocationDTO[]
}

function getNoteUpdateObject(props: getNoteUpdateObjectProps) {
  const { data, model, serviceOptions } = props
  const { serviceLocation, code } = model
  data.locationId =
    serviceLocation &&
    serviceOptions.filter(
      (location) => location.id === Number(serviceLocation),
    )[0]?.externalLinkId
  data.noteCode = code
  data.locationCode =
    serviceLocation &&
    serviceOptions.filter(
      (location) => location.id === Number(serviceLocation),
    )[0]?.locationCode

  return data
}
